import {
	ErrorModal,
	useUserContext,
	AssetTreePicker,
	useAssetContext,
	CreateNewAssetDialog,
	EditAssetDialog,
	RemoveAssetDialog,
	searchAssets,
	useHasPermission,
} from "@dexteel/mesf-core";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HiddenChildrenDialog } from "./components/dialog-hidde-childrens";
import {
	MapCreateTemplate,
	MapCreateWorkOrder,
} from "./components/map-create-dialogs";
import { useMapContext } from "./context/MapContext";
import { useContextMenuNavigation } from "./hooks/useContextMenuNavigation";
import { useLoadMapProps } from "./hooks/useLoadMap";
import { useURLChanged } from "./hooks/useURLChanged";
import Grid2 from "@mui/material/Grid2";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import BuildIcon from "@mui/icons-material/Build";
import SubjectIcon from "@mui/icons-material/Subject";
import AttachmentIcon from "@mui/icons-material/Attachment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WidgetsIcon from "@mui/icons-material/Widgets";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { mesfColors } from "../../../setup/ag-grid/mesf-theme";
import { getAssetPath } from "../../shared/repository/mms-repository";
import { get, isNil } from "lodash-es";

const useStyles = makeStyles((theme) => ({
	contextMenu: {
		backgroundColor: "#F8F8F8",
		borderRadius: "4px",
		padding: "0",
		width: "170px",
		margin: "0",
		position: "absolute",
		listStyle: "none",
		boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
		zIndex: 1000,
	},
	menuItem: {
		padding: "6px 12px 6px 10px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		backgroundColor: "#F8F8F8",
		fontSize: "14px",
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover": {
			backgroundColor: "#d4edff",
		},
	},
	menuSeparator: {
		height: "1px",
		backgroundColor: "#BABFC7",
		margin: "4px 0",
	},
	hiddenButton: {
		backgroundColor: "#c8212ccf",
		color: "#FFF",
		borderRadius: "2px",
		padding: "8px 12px",
		width: "auto",
		minWidth: "120px",
		height: "auto",
		margin: "0",
		listStyle: "none",
		position: "absolute",
		textAlign: "center",
		right: 20,
		top: 80,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1000,
		fontSize: "12px",
		fontWeight: 600,
	},
	topBar: {
		display: "flex",
		alignItems: "center",
		gap: "16px",
		marginBottom: "16px",
		backgroundColor: mesfColors.background.paper,
		padding: "12px 20px",
		borderRadius: "8px",
		boxShadow: mesfColors.shadows[2],
	},
	treePickerSection: {
		flex: "0 0 340px",
		minWidth: "340px",
		maxWidth: "340px",
	},
	breadcrumbSection: {
		flex: 1,
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: "8px",
		fontSize: "15px",
		color: mesfColors.text.secondary,
		paddingLeft: "20px",
		borderLeft: `2px solid ${mesfColors.grey.divider}`,
		minWidth: 0,
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
	},
	breadcrumbItem: {
		display: "inline-block",
		color: mesfColors.text.primary,
		textDecoration: "none",
		transition: "all 0.2s ease",
		cursor: "pointer",
		fontWeight: 400,
		fontSize: "13px",
		whiteSpace: "nowrap",
		padding: "4px 8px",
		borderRadius: "4px",
		"&:hover": {
			backgroundColor: mesfColors.grey[100],
			color: mesfColors.text.primary,
		},
	},
	breadcrumbSeparator: {
		display: "inline-block",
		color: mesfColors.text.secondary,
		margin: "0 8px",
		fontSize: "18px",
		fontWeight: 400,
		whiteSpace: "nowrap",
	},
	breadcrumbCurrent: {
		color: mesfColors.text.primary,
		fontWeight: 600,
	},
	currentBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: "4px",
		padding: "6px 12px",
		backgroundColor: mesfColors.grey[100],
		border: `1px solid ${mesfColors.grey.divider}`,
		borderRadius: "4px",
		fontSize: "13px",
		marginLeft: "8px",
		cursor: "pointer",
		transition: "all 0.2s ease",
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
		"&:hover": {
			boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
			transform: "translateY(-1px)",
			backgroundColor: mesfColors.grey[50],
		},
	},
	currentLabel: {
		color: mesfColors.text.secondary,
		fontWeight: 500,
		textTransform: "uppercase",
		fontSize: "11px",
		letterSpacing: "0.3px",
	},
	currentName: {
		color: mesfColors.text.primary,
		fontWeight: 600,
		fontSize: "15px",
		textTransform: "uppercase",
		paddingRight: "4px",
	},
	rmBadge: {
		padding: "4px 10px",
		backgroundColor: mesfColors.map.rmOpenOrders,
		color: mesfColors.common.white,
		borderRadius: "3px",
		fontSize: "11px",
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	pmBadge: {
		padding: "4px 8px",
		backgroundColor: "rgb(0, 138, 0)", // Same green as SVG
		color: mesfColors.common.white,
		borderRadius: "3px",
		fontSize: "11px",
		fontWeight: 600,
		display: "inline-flex",
		alignItems: "center",
		minWidth: "30px",
		justifyContent: "center",
	},
	rmBadgeSimple: {
		padding: "4px 8px",
		backgroundColor: "rgb(240, 163, 10)", // Same orange as SVG
		color: mesfColors.common.white,
		borderRadius: "3px",
		fontSize: "11px",
		fontWeight: 600,
		display: "inline-flex",
		alignItems: "center",
		minWidth: "30px",
		justifyContent: "center",
	},
	hamburgerIcon: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		marginLeft: "8px",
		padding: "6px",
		backgroundColor: mesfColors.text.secondary,
		borderRadius: "4px",
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: mesfColors.text.primary,
			transform: "scale(1.05)",
		},
		"& svg line": {
			stroke: mesfColors.common.white,
		},
	},
}));

export const MapComponent = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: { allAssets },
	} = useAssetContext();

	const { data: allAssetsForCRUD } = searchAssets();

	const didMountRef = useRef(false);
	const navigate = useNavigate();
	const [error, setError] = useState("");
	const [assetPath, setAssetPath] = useState("");
	const [pathIds, setPathIds] = useState<number[]>([]);
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
	const [selectedAssetName, setSelectedAssetName] = useState<string | null>(
		null,
	);
	// Asset a new work order / template is being created for; null = closed
	const [workOrderAssetId, setWorkOrderAssetId] = useState<number | null>(null);
	const [templateAssetId, setTemplateAssetId] = useState<number | null>(null);
	const hasPermissionTo = useHasPermission();
	const canCreateWorkOrder = hasPermissionTo("MMS.UpsertWorkOrder");
	const canCreateTemplate = hasPermissionTo("MMS.UpsertTemplate");
	const classes = useStyles();
	const {
		state: {
			anchorPoint: { x, y },
			loadingMap,
			showContextMenu,
			currAssetId,
			menuContextAssetId,
			showModalHiddenChildrens,
			showHiddenChildrenButton,
			assetNameFromApi,
			RMWorkOrdersFromApi,
			PMWorkOrdersFromApi,
		},
		actions: {
			setShowContextMenu,
			setMenuContextAssetId,
			setCurrAssetId,
			setAnchorPointY,
			setAnchorPointX,
			setShowModalHiddenChildrens,
			setListHiddenChildrenIds,
			setShowHiddenChildrenButton,
		},
	} = useMapContext();

	const URLChanged = useURLChanged();

	const location = useLocation();

	const handleClick = (e: any) => {
		if (showContextMenu) {
			setShowContextMenu(false);
			setMenuContextAssetId(undefined);
		}
	};

	const {
		handleClickAssetDetails,
		handleClickAssetDocuments,
		handleClickAssetParts,
		handleClickAssetAssemblies,
		handleClickAssetLOTO,
		handleClickAttachment,
		handleClickAssetText,
		handleClickWorkOrders,
		handleClickQCBD,
	} = useContextMenuNavigation();

	const loadMapProps = useLoadMapProps({ setError });

	const handleContextMenu = (e: any) => {
		e.preventDefault();
		setAnchorPointX(e.pageX);
		setAnchorPointY(e.pageY);
		setShowContextMenu(true);
		setMenuContextAssetId(currAssetId);
	};

	const handleAssetChange = (value: number | null) => {
		if (value !== null && value !== currAssetId) {
			setCurrAssetId(value);
		}
	};

	// Helper to get asset name from the complete assets list
	const getAssetName = (assetId: number | null | undefined): string => {
		if (!assetId) return "this asset";

		// Use allAssetsForCRUD (from searchAssets hook) which has ALL assets
		const asset = allAssetsForCRUD?.find(
			(a: Asset) => Number(a.AssetId) === Number(assetId),
		);
		return asset?.AssetName ?? "this asset";
	};

	// Check if asset can be deleted (only leafs, not parents)
	const canDeleteAsset = (assetId: number | null | undefined): boolean => {
		if (!assetId || !allAssetsForCRUD) return false;
		const asset = allAssetsForCRUD?.find(
			(a: Asset) => Number(a.AssetId) === Number(assetId),
		);
		if (!asset) return false;

		// Can only delete if it's a leaf (IsBranch = false means no children)
		return asset.IsBranch !== true;
	};

	// CRUD dialog handlers
	const handleOpenCreateDialog = () => {
		const targetId = menuContextAssetId ?? currAssetId;
		const assetIdNumber =
			targetId !== null && targetId !== undefined ? Number(targetId) : null;
		setSelectedAssetId(assetIdNumber);
		setSelectedAssetName(null);
		setShowCreateDialog(true);
		setShowContextMenu(false);
	};

	const handleOpenEditDialog = () => {
		const targetId = menuContextAssetId ?? currAssetId;
		const assetIdNumber =
			targetId !== null && targetId !== undefined ? Number(targetId) : null;
		const assetName = getAssetName(assetIdNumber);
		setSelectedAssetId(assetIdNumber);
		setSelectedAssetName(assetName);
		setShowEditDialog(true);
		setShowContextMenu(false);
	};

	const handleOpenDeleteDialog = () => {
		const targetId = menuContextAssetId ?? currAssetId;
		const assetIdNumber =
			targetId !== null && targetId !== undefined ? Number(targetId) : null;
		const assetName = getAssetName(assetIdNumber);
		setSelectedAssetId(assetIdNumber);
		setSelectedAssetName(assetName);
		setShowDeleteDialog(true);
		setShowContextMenu(false);
	};

	// Read the asset now: the page click that follows clears menuContextAssetId
	const getMenuAssetId = (): number | null => {
		const targetId = menuContextAssetId ?? currAssetId;
		return targetId !== null && targetId !== undefined
			? Number(targetId)
			: null;
	};

	const handleOpenCreateWorkOrder = () => {
		setWorkOrderAssetId(getMenuAssetId());
		setShowContextMenu(false);
	};

	const handleOpenCreateTemplate = () => {
		setTemplateAssetId(getMenuAssetId());
		setShowContextMenu(false);
	};

	const handleCloseCreateDialog = (shouldUpdate: boolean) => {
		setShowCreateDialog(false);
		if (shouldUpdate) {
			loadMapProps();
		}
	};

	const handleCloseEditDialog = (shouldUpdate: boolean) => {
		setShowEditDialog(false);
		if (shouldUpdate) {
			loadMapProps();
		}
	};

	const handleCloseDeleteDialog = (shouldUpdate: boolean) => {
		setShowDeleteDialog(false);
		if (shouldUpdate) {
			loadMapProps();
		}
	};

	useEffect(() => {
		if (didMountRef.current) {
			// Close context menu when changing assets
			setShowContextMenu(false);
			setMenuContextAssetId(undefined);
			loadMapProps();
		}
		didMountRef.current = true;
	}, [currAssetId]);

	useEffect(() => {
		setCurrAssetId(defaultAreaId);
	}, [defaultAreaId]);

	useEffect(() => {
		const currentPath = location.pathname;

		if (currentPath.endsWith("/map/")) {
			setCurrAssetId(defaultAreaId);
		} else if (currentPath.endsWith("/home")) {
			navigate("/map/");
		} else if (currentPath.includes("/map/") && currentPath !== "/map/") {
			URLChanged();
		}
	}, [location.pathname, defaultAreaId]);

	// Close context menu when navigating away from map
	useEffect(() => {
		if (!location.pathname.includes("/map")) {
			setShowContextMenu(false);
			setMenuContextAssetId(undefined);
		}
	}, [location.pathname]);

	useEffect(() => {
		setShowHiddenChildrenButton(false);
		setListHiddenChildrenIds([]);
	}, [currAssetId]);

	useEffect(() => {
		const main = document.getElementById("main") as SVGSVGElement | null;
		if (main) {
			const contentHeight = main.getBBox().height;
			main.setAttribute("height", contentHeight.toString());
		}
	}, [loadingMap, currAssetId, defaultAreaId, location.pathname]);

	useEffect(() => {
		if (currAssetId !== undefined && currAssetId !== null) {
			getAssetPath(currAssetId)
				.then((res) => {
					if (res.ok) {
						const fullPath = get(res, "data.tables[0].rows[0].FullPath", "");
						const pathIdsStr = get(res, "data.tables[0].rows[0].PathIds", "");
						const idsArray = pathIdsStr
							? pathIdsStr.split(",").map(Number)
							: [];
						setAssetPath(fullPath || "");
						setPathIds(idsArray);
					} else {
						setAssetPath("");
						setPathIds([]);
					}
				})
				.catch((error) => {
					console.error("Error fetching asset path:", error);
					setAssetPath("");
					setPathIds([]);
				});
		} else {
			setAssetPath("");
			setPathIds([]);
		}
	}, [currAssetId]);

	return (
		<>
			<div
				onClick={handleClick}
				style={{
					backgroundColor: "#ffffff",
					minHeight: "calc(100vh - 64px)",
				}}
			>
				<div onContextMenu={handleContextMenu}>
					{!loadingMap && assetPath && pathIds.length > 0 && (
						<div className={classes.topBar}>
							<div className={classes.treePickerSection}>
								<AssetTreePicker
									label="Asset"
									value={
										currAssetId !== null && currAssetId !== undefined
											? Number(currAssetId)
											: null
									}
									onChange={handleAssetChange}
								/>
							</div>
							<div className={classes.breadcrumbSection}>
								{assetPath
									.split("/")
									.slice(0, -1)
									.map((part, index) => (
										<span
											key={`path-${index}`}
											style={{ display: "inline-flex", alignItems: "center" }}
										>
											<span
												className={classes.breadcrumbItem}
												onClick={() => {
													const targetAssetId = pathIds[index];
													if (
														targetAssetId !== undefined &&
														targetAssetId !== null
													) {
														setCurrAssetId(targetAssetId);
													}
												}}
											>
												{part}
											</span>
											<span className={classes.breadcrumbSeparator}>›</span>
										</span>
									))}

								{/* CURRENT Badge */}
								<div
									className={classes.currentBadge}
									onClick={(e) => {
										// Only navigate if not clicking the hamburger icon
										if (
											!(e.target as HTMLElement).closest(
												`.${classes.hamburgerIcon}`,
											)
										) {
											navigate(
												`/asset-details/${currAssetId}?resource=documents&assets=true&statistics=true&workOrders=true`,
											);
										}
									}}
								>
									<span className={classes.currentLabel}>CURRENT:</span>
									<span className={classes.currentName}>
										{assetNameFromApi || assetPath.split("/").slice(-1)[0]}
									</span>

									{/* PM Badge - Always visible */}
									<span className={classes.pmBadge} title="PM Open Orders">
										{PMWorkOrdersFromApi || 0}
									</span>

									{/* RM Badge - Always visible */}
									<span
										className={classes.rmBadgeSimple}
										title="RM Open Orders"
									>
										{RMWorkOrdersFromApi || 0}
									</span>

									<div
										className={classes.hamburgerIcon}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											const rect = (
												e.currentTarget as HTMLElement
											).getBoundingClientRect();
											setAnchorPointX(rect.right);
											setAnchorPointY(rect.bottom + 5);
											setMenuContextAssetId(currAssetId);
											setShowContextMenu(true);
										}}
									>
										<svg width="16" height="12" viewBox="0 0 16 12" fill="none">
											<line
												x1="0"
												y1="1"
												x2="16"
												y2="1"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
											/>
											<line
												x1="0"
												y1="6"
												x2="16"
												y2="6"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
											/>
											<line
												x1="0"
												y1="11"
												x2="16"
												y2="11"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
											/>
										</svg>
									</div>
								</div>
							</div>
						</div>
					)}

					{loadingMap ? (
						<Box
							sx={{
								display: "flex",
								position: "absolute",
								left: "45%",
								top: "30%",
							}}
						>
							<CircularProgress size={200} />
							<div style={{ marginTop: "34%", marginLeft: "-50%" }}>
								Loading...
							</div>
						</Box>
					) : (
						<>
							<svg
								width="100%"
								height="auto"
								style={{ minHeight: 860 }}
								viewBox="-0.5 -1 1919 860"
								id="main"
								preserveAspectRatio="xMinYMin meet"
							/>

							{showHiddenChildrenButton && (
								<ul className={classes.hiddenButton}>
									<MenuItem
										style={{
											fontSize: "12px",
											textAlign: "center",
											margin: "0 auto",
										}}
										onClick={() => setShowModalHiddenChildrens(true)}
									>
										Hidden Children
									</MenuItem>
								</ul>
							)}
							<HiddenChildrenDialog
								show={showModalHiddenChildrens}
								onHide={() => setShowModalHiddenChildrens(false)}
							/>
						</>
					)}
				</div>

				{showContextMenu ? (
					<ul
						className={classes.contextMenu}
						style={{ top: y, left: x, padding: "6px 0" }}
					>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetDetails}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<FindInPageIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Asset Details</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickWorkOrders}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<AssignmentIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Work Orders</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetDocuments}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<EventNoteIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Drawings</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetParts}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<BuildIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Parts</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetAssemblies}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<WidgetsIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Assemblies</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetLOTO}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<DescriptionIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>LOTO</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAttachment}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<AttachmentIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Attachments</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleClickAssetText}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<SubjectIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Text</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem className={classes.menuItem} onClick={handleClickQCBD}>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2 size={{ md: 2, xs: 3 }}>
									<AttachmentIcon style={{ width: 18 }} />
								</Grid2>
								<Grid2 size={{ md: 9, xs: 9 }}>
									<div style={{ marginLeft: 5 }}>Asset QCBD</div>
								</Grid2>
							</Grid2>
						</MenuItem>

						{(canCreateWorkOrder || canCreateTemplate) && (
							<div className={classes.menuSeparator} />
						)}
						{canCreateWorkOrder && (
							<MenuItem
								className={classes.menuItem}
								onClick={handleOpenCreateWorkOrder}
							>
								<Grid2 container style={{ alignItems: "center" }}>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 2, xs: 3 }}
									>
										<NoteAddIcon style={{ width: 18, height: 18 }} />
									</Grid2>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 9, xs: 9 }}
									>
										<div style={{ marginLeft: 5 }}>Create Work Order</div>
									</Grid2>
								</Grid2>
							</MenuItem>
						)}
						{canCreateTemplate && (
							<MenuItem
								className={classes.menuItem}
								onClick={handleOpenCreateTemplate}
							>
								<Grid2 container style={{ alignItems: "center" }}>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 2, xs: 3 }}
									>
										<EventRepeatIcon style={{ width: 18, height: 18 }} />
									</Grid2>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 9, xs: 9 }}
									>
										<div style={{ marginLeft: 5 }}>Create Template</div>
									</Grid2>
								</Grid2>
							</MenuItem>
						)}

						<div className={classes.menuSeparator} />

						<MenuItem
							className={classes.menuItem}
							onClick={handleOpenCreateDialog}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2
									style={{ display: "flex", alignItems: "center" }}
									size={{ md: 2, xs: 3 }}
								>
									<AddIcon style={{ width: 18, height: 18 }} />
								</Grid2>
								<Grid2
									style={{ display: "flex", alignItems: "center" }}
									size={{ md: 9, xs: 9 }}
								>
									<div style={{ marginLeft: 5 }}>Create Asset</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						<MenuItem
							className={classes.menuItem}
							onClick={handleOpenEditDialog}
						>
							<Grid2 container style={{ alignItems: "center" }}>
								<Grid2
									style={{ display: "flex", alignItems: "center" }}
									size={{ md: 2, xs: 3 }}
								>
									<EditIcon style={{ width: 18, height: 18 }} />
								</Grid2>
								<Grid2
									style={{ display: "flex", alignItems: "center" }}
									size={{ md: 9, xs: 9 }}
								>
									<div style={{ marginLeft: 5 }}>Edit Asset</div>
								</Grid2>
							</Grid2>
						</MenuItem>
						{canDeleteAsset(
							(menuContextAssetId ?? currAssetId) !== null &&
								(menuContextAssetId ?? currAssetId) !== undefined
								? Number(menuContextAssetId ?? currAssetId)
								: null,
						) && (
							<MenuItem
								className={classes.menuItem}
								onClick={handleOpenDeleteDialog}
							>
								<Grid2 container style={{ alignItems: "center" }}>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 2, xs: 3 }}
									>
										<DeleteIcon style={{ width: 18, height: 18 }} />
									</Grid2>
									<Grid2
										style={{ display: "flex", alignItems: "center" }}
										size={{ md: 9, xs: 9 }}
									>
										<div style={{ marginLeft: 5 }}>Delete Asset</div>
									</Grid2>
								</Grid2>
							</MenuItem>
						)}
					</ul>
				) : (
					""
				)}
				<ErrorModal error={error} onHide={() => setError("")} />

				<CreateNewAssetDialog
					assetNodeSelectedInTree={
						selectedAssetId !== null
							? {
									id: selectedAssetId,
									parent: 0,
									text: selectedAssetName ?? "",
									isAtTop: false,
									isAtBottom: false,
									isEditable: true,
									sortOrder: null,
									data: { fileType: "", fileSize: "" },
									droppable: true,
								}
							: null
					}
					show={showCreateDialog}
					onHide={handleCloseCreateDialog}
				/>
				<EditAssetDialog
					selectedAssetId={selectedAssetId}
					show={showEditDialog}
					onHide={handleCloseEditDialog}
				/>
				<RemoveAssetDialog
					selectedAssetId={selectedAssetId}
					selectedAssetName={selectedAssetName}
					show={showDeleteDialog}
					onHide={handleCloseDeleteDialog}
				/>
			</div>
			{/* Outside the page div: clicks inside a dialog bubble through the portal */}
			<MapCreateWorkOrder
				assetId={workOrderAssetId}
				onClose={() => setWorkOrderAssetId(null)}
			/>
			<MapCreateTemplate
				assetId={templateAssetId}
				onClose={() => setTemplateAssetId(null)}
			/>
		</>
	);
};
